import { useCallback, useEffect, useMemo, useState } from 'react'
import type {
  CalendarConnection,
  Destination,
  EventCandidate,
  EventConflict,
  FamilyMember,
  FreeWindow,
  LinkedCalendar,
  PlannerEvent,
  ScanRule,
  ScoredDeal,
  TravelDeal,
} from '../../types/family-planner'
import {
  detectConflicts,
  findDuplicateEvent,
  findFreeWindows,
  mergeDuplicates,
  scoreDeals,
  type AssistantContext,
} from '../../lib/family-planner-analytics'
import {
  completePendingOAuth,
  listCalendars,
  listCandidates,
  listConnections,
  listDeals,
  listDestinations,
  listEvents,
  listMembers,
  listScanRules,
} from '../../lib/family-planner-api'

// One hook shared by every tab: loads the raw data once, then derives the
// aggregated calendar, conflicts, free windows, and scored deals client-side so
// the whole module stays consistent no matter which tab is open.

export const PLANNING_HORIZON_DAYS = 75

export interface PlannerState {
  loading: boolean
  members: FamilyMember[]
  connections: CalendarConnection[]
  calendars: LinkedCalendar[]
  /** Every event from every linked calendar, before filtering */
  allEvents: PlannerEvent[]
  /** Enabled calendars only, cross-calendar duplicates collapsed */
  events: PlannerEvent[]
  conflicts: EventConflict[]
  windows: FreeWindow[]
  rules: ScanRule[]
  candidates: EventCandidate[]
  pendingCandidates: EventCandidate[]
  destinations: Destination[]
  deals: TravelDeal[]
  scoredDeals: ScoredDeal[]
  assistantContext: AssistantContext
  calendarById: (id: string) => LinkedCalendar | undefined
  memberById: (id: string) => FamilyMember | undefined
  /** Set when a provider sign-in that redirected away came back with a failure */
  linkError: string | null
  /** Set when a redirect-based sign-in has just completed successfully */
  linkNotice: string | null
  refresh: () => void
}

export function usePlanner(): PlannerState {
  const [loading, setLoading] = useState(true)
  const [members, setMembers] = useState<FamilyMember[]>([])
  const [connections, setConnections] = useState<CalendarConnection[]>([])
  const [calendars, setCalendars] = useState<LinkedCalendar[]>([])
  const [allEvents, setAllEvents] = useState<PlannerEvent[]>([])
  const [rules, setRules] = useState<ScanRule[]>([])
  const [candidates, setCandidates] = useState<EventCandidate[]>([])
  const [destinations, setDestinations] = useState<Destination[]>([])
  const [deals, setDeals] = useState<TravelDeal[]>([])
  const [reloadKey, setReloadKey] = useState(0)
  const [linkError, setLinkError] = useState<string | null>(null)
  const [linkNotice, setLinkNotice] = useState<string | null>(null)

  // A Microsoft sign-in navigates away and lands back here with a code in the
  // query string. Finish that exchange before the first load so the new account
  // is already present when the tabs render.
  useEffect(() => {
    let cancelled = false
    completePendingOAuth()
      .then((connection) => {
        if (cancelled || !connection) return
        setLinkNotice(`${connection.accountEmail} linked — ${connection.calendarIds.length} calendars synced.`)
        setReloadKey((k) => k + 1)
      })
      .catch((err) => {
        if (!cancelled) setLinkError(err instanceof Error ? err.message : 'Sign-in could not be completed.')
      })
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    Promise.all([
      listMembers(),
      listConnections(),
      listCalendars(),
      listEvents(),
      listScanRules(),
      listCandidates(),
      listDestinations(),
      listDeals(),
    ])
      .then(([mem, conns, cals, evts, scanRules, cands, dests, travelDeals]) => {
        if (cancelled) return
        setMembers(mem)
        setConnections(conns)
        setCalendars(cals)
        setAllEvents(evts)
        setRules(scanRules)
        setCandidates(cands)
        setDestinations(dests)
        setDeals(travelDeals)
        setLoading(false)
      })
      .catch(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [reloadKey])

  const refresh = useCallback(() => setReloadKey((k) => k + 1), [])

  const events = useMemo(() => {
    const enabled = new Set(calendars.filter((c) => c.enabled).map((c) => c.id))
    return mergeDuplicates(allEvents.filter((e) => enabled.has(e.calendarId)))
  }, [allEvents, calendars])

  const conflicts = useMemo(() => detectConflicts(events), [events])

  const windows = useMemo(
    () => findFreeWindows(events, { minNights: 3, horizonDays: PLANNING_HORIZON_DAYS }),
    [events],
  )

  const scoredDeals = useMemo(
    () => scoreDeals(deals, destinations, windows, events),
    [deals, destinations, windows, events],
  )

  // Flag inbox rows that the calendar already covers so nothing gets booked twice.
  const pendingCandidates = useMemo(
    () =>
      candidates
        .filter((c) => c.status === 'pending')
        .map((c) => {
          const dupe = findDuplicateEvent(c.parsed, events)
          return dupe ? { ...c, duplicateOfEventId: dupe.id } : c
        }),
    [candidates, events],
  )

  const assistantContext = useMemo<AssistantContext>(
    () => ({ events, conflicts, windows, deals: scoredDeals }),
    [events, conflicts, windows, scoredDeals],
  )

  const calendarById = useCallback((id: string) => calendars.find((c) => c.id === id), [calendars])
  const memberById = useCallback((id: string) => members.find((m) => m.id === id), [members])

  return {
    loading,
    members,
    connections,
    calendars,
    allEvents,
    events,
    conflicts,
    windows,
    rules,
    candidates,
    pendingCandidates,
    destinations,
    deals,
    scoredDeals,
    assistantContext,
    calendarById,
    memberById,
    linkError,
    linkNotice,
    refresh,
  }
}

// ─── Shared presentation bits ────────────────────────────────

export const CATEGORY_META: Record<string, { icon: string; label: string }> = {
  school: { icon: '🎒', label: 'School' },
  sports: { icon: '⚽', label: 'Sports' },
  medical: { icon: '🩺', label: 'Medical' },
  work: { icon: '💼', label: 'Work' },
  social: { icon: '🎉', label: 'Social' },
  travel: { icon: '✈️', label: 'Travel' },
  other: { icon: '📌', label: 'Other' },
}

export const PROVIDER_META: Record<string, { label: string; icon: string }> = {
  google: { label: 'Google', icon: '🟢' },
  microsoft: { label: 'Microsoft', icon: '🔷' },
  ics: { label: 'Subscribed feed', icon: '🔗' },
  local: { label: 'Local', icon: '📁' },
}

export function money(value: number): string {
  return value.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 })
}
